import { resolveAccountCreatedIdentity } from './accountCreatedEnrichment';
import { isShareEvent } from './shareEvents';

/**
 * Stable key for unique-visitor counting and drill-down grouping.
 * Prefer resolved email so the same person with multiple Mongo ids / sessions
 * counts once; fall back to guest session or meta.user for anonymous visitors.
 */
export const visitorKeyForRow = (row, allRows = [], directory = null) => {
  if (!row) return null;

  const { email } = resolveAccountCreatedIdentity(row, allRows, directory);
  if (email) return `email:${email.trim().toLowerCase()}`;

  const sessionKey =
    row?.meta?.guestSessionId ||
    (row?.meta?.user &&
    row.meta.user !== 'guest' &&
    row.meta.user !== 'anonymous'
      ? row.meta.user
      : null);

  return sessionKey ? `session:${sessionKey}` : null;
};

/** Raw browser session id before email-based merge (guestSessionId or meta.user). */
export const visitorSessionKeyForRow = (row) => {
  const sessionKey =
    row?.meta?.guestSessionId ||
    (row?.meta?.user &&
    row.meta.user !== 'guest' &&
    row.meta.user !== 'anonymous'
      ? row.meta.user
      : null);
  return sessionKey ? String(sessionKey) : null;
};

/** True when a merged visitor row came from more than one distinct session. */
export const isReturningVisitorGroup = (sessionKeys) =>
  sessionKeys instanceof Set ? sessionKeys.size > 1 : (sessionKeys?.size ?? 0) > 1;

/**
 * Group rows into one entry per resolved visitor — the single source of truth
 * shared by the Unique Visitors KPI tile and its drill-down so the headline
 * count can never disagree with the list it links to.
 *
 * A group is kept only when it has at least one `webview_opened` (an actual
 * visit), matching the headline definition. Identity is resolved against
 * `[...rows, ...contextRows]` so guest sessions merge with the person's
 * email-bearing events; pass the same `directory` everywhere for consistency.
 *
 * `galleryUrlFor` / `isValidGalleryUrl` are optional hooks for the drill-down's
 * gallery-link enrichment; the KPI tile omits them and only reads the length.
 */
export const buildVisitorGroups = (
  rows,
  {
    contextRows = [],
    directory = null,
    galleryUrlFor = null,
    isValidGalleryUrl = null,
  } = {}
) => {
  const list = Array.isArray(rows) ? rows : [];
  const ctx = Array.isArray(contextRows) ? contextRows : [];
  const identityRows = [...list, ...ctx];
  const map = new Map();

  list.forEach((row) => {
    const event = row?.meta?.event;
    const key = visitorKeyForRow(row, identityRows, directory);
    if (!key) return;

    if (!map.has(key)) {
      map.set(key, {
        key,
        anchorRow: row,
        opens: 0,
        shuffleCount: 0,
        shareCount: 0,
        downloadCount: 0,
        firstSeen: null,
        lastSeen: null,
        gallerySet: new Set(),
        lastGalleryUrl: null,
        sessionKeys: new Set(),
      });
    }
    const g = map.get(key);

    const sessionKey = visitorSessionKeyForRow(row);
    if (sessionKey) g.sessionKeys.add(sessionKey);

    const ts = new Date(row.timestamp || 0).getTime();
    if (Number.isFinite(ts)) {
      if (g.firstSeen == null || ts < new Date(g.firstSeen).getTime()) g.firstSeen = row.timestamp;
      if (g.lastSeen == null || ts > new Date(g.lastSeen).getTime()) g.lastSeen = row.timestamp;
    }

    if (event === 'webview_opened') {
      g.opens += 1;
      if (typeof galleryUrlFor === 'function') {
        const gUrl = galleryUrlFor(row);
        if (gUrl && (typeof isValidGalleryUrl !== 'function' || isValidGalleryUrl(gUrl))) {
          g.gallerySet.add(gUrl);
          g.lastGalleryUrl = gUrl;
        }
      }
      // Prefer a webview_opened row as the anchor (it always has a session key)
      // so the drill-down header resolves correctly.
      g.anchorRow = row;
    } else if (event === 'shuffle_room') g.shuffleCount += 1;
    else if (isShareEvent(event)) g.shareCount += 1;
    else if (event === 'download_layout') g.downloadCount += 1;
  });

  return [...map.values()]
    .filter((g) => g.opens > 0)
    .map((g) => ({
      ...g,
      isReturning: isReturningVisitorGroup(g.sessionKeys),
      sessionCount: g.sessionKeys.size,
    }))
    .sort((a, b) => new Date(b.lastSeen || 0) - new Date(a.lastSeen || 0));
};

/** Number of distinct visitors — the drill-down group count, by construction. */
export const countVisitorGroups = (rows, opts = {}) =>
  buildVisitorGroups(rows, opts).length;

export const countUniqueVisitors = (feedRows, opts = {}) => {
  const rows = Array.isArray(feedRows) ? feedRows : [];
  if (rows.length === 0) return null;

  const { allRows = rows, directory = null } = opts;
  return countVisitorGroups(rows, { contextRows: allRows, directory });
};
